import React from "react";
import "./Manual.css";
import "../App.css";

const Manual = () => {
  return (
    <div className="text-page">
      <header className="header">
        <h1 className="SubTitle">Admin &amp; Content Editor Manual</h1>
      </header>
      <main className="content manual-page">
        <section className="text-section">
          <h2>1. Logging In</h2>
          <p>
            All content-management pages require you to be logged in. Click
            the login control in the navigation bar and sign in with your
            account.
          </p>
          <ul>
            <li>
              News publishing is additionally restricted to a short list of
              approved editor accounts. If you can log in but still can't
              reach the News editor, your account has not been added to that
              list yet &ndash; contact the site administrator.
            </li>
            <li>
              Document uploading (the Upload page) is open to any logged-in
              account &ndash; no separate approval needed.
            </li>
          </ul>
        </section>

        <section className="text-section">
          <h2>2. Managing News Articles</h2>
          <p>
            Page: <strong>/settings</strong> (menu: Settings) &ndash; only
            approved editor accounts can use this page.
          </p>

          <h3>Publishing a new article</h3>
          <ol>
            <li>Go to Settings.</li>
            <li>
              Fill in the <strong>Title</strong>, the <strong>Content</strong>{" "}
              (written with the rich-text editor &ndash; supports headings,
              bold/italic, links, and bulleted or numbered lists), and
              optionally an <strong>Image</strong>. Articles without an image
              still publish fine and show a plain text card instead of a
              photo.
            </li>
            <li>
              The Author field is filled in automatically from your account.
            </li>
            <li>Click &ldquo;Publish News Post&rdquo;.</li>
          </ol>

          <h3>Editing or deleting an article</h3>
          <ul>
            <li>
              Scroll to &ldquo;Recent News Posts&rdquo; at the bottom of the
              Settings page.
            </li>
            <li>
              Click <strong>Edit</strong> to load an article back into the
              form (title and content are pre-filled; pick a new image only
              if you want to replace the existing one &ndash; leave it empty
              to keep the current image).
            </li>
            <li>
              Click <strong>Delete</strong> to remove an article permanently.
              This cannot be undone.
            </li>
          </ul>

          <h3>Where published articles show up</h3>
          <ul>
            <li>
              Home page, &ldquo;Latest News&rdquo; section &ndash; newest
              article first, every article listed.
            </li>
            <li>
              <strong>/allnews</strong> &ndash; a full list of every article
              ever published.
            </li>
            <li>
              <strong>/news/&lt;id&gt;</strong> &ndash; the full single-article
              page.
            </li>
          </ul>
        </section>

        <section className="text-section">
          <h2>3. Uploading Documents</h2>
          <p>
            Page: <strong>/Upload</strong> (menu: Documents &gt; Upload)
            &ndash; any logged-in account can use this page.
          </p>
          <p>
            Accepted file types: PDF, PPT/PPTX, DOC/DOCX, XLS/XLSX, CSV, TXT,
            JPG/JPEG, PNG.
          </p>
          <ol>
            <li>Go to Documents &gt; Upload.</li>
            <li>
              Choose one or more files. If you choose more than one, you will
              be asked for a Folder Name to group them under.
            </li>
            <li>
              (Optional) Select a Work Package (WP1&ndash;WP6) this file
              belongs to.
            </li>
            <li>
              Tick any categories that apply &ndash; see section 4 below for
              what each one does. You can tick more than one.
            </li>
            <li>
              Fill in the <strong>Title</strong> (a short descriptive name for
              the file) and <strong>Country</strong>, if applicable. Author
              and Upload Date are filled in automatically.
            </li>
            <li>Click Upload.</li>
          </ol>

          <div className="manual-warning">
            <strong>Public Document checkbox &ndash; read this carefully.</strong>
            <p>
              Ticking &ldquo;Public Document&rdquo; makes the file visible to
              everyone who visits the site, including people who are not
              logged in (it appears on the public Public Documents page). The
              form will ask you to confirm before submitting if this box is
              ticked. Leave it unticked for anything that should stay
              internal to logged-in project members.
            </p>
          </div>
        </section>

        <section className="text-section">
          <h2>4. What Each Category / Menu Item Means</h2>
          <p>
            These checkboxes on the Upload page control which menu item(s)
            under &ldquo;Documents&rdquo; a file will later appear under:
          </p>
          <ul>
            <li>
              <strong>Public Document</strong> &rarr; Documents &gt; Public
              Documents. No login required to view.
            </li>
            <li>
              <strong>Deliverable</strong> &rarr; Documents &gt; Deliverables.
              If also marked Public, it additionally shows under Documents
              &gt; Deliverables (public ones).
            </li>
            <li>
              <strong>Meeting</strong> &rarr; Documents &gt; Meetings.
            </li>
            <li>
              <strong>Contact List</strong> &rarr; Documents &gt; Contact
              Lists.
            </li>
            <li>
              <strong>Promotion Material</strong> &rarr; Documents &gt;
              Promotion materials.
            </li>
            <li>
              <strong>Report</strong> &rarr; Documents &gt; Reports.
            </li>
            <li>
              <strong>Publication</strong> &rarr; Documents &gt; Publications.
            </li>
            <li>
              <strong>Template</strong> &rarr; Documents &gt; Templates.
            </li>
            <li>
              <strong>Work Package (WP)</strong> &rarr; Documents &gt; WP1
              &hellip; WP6, matching whichever work package you selected on
              upload.
            </li>
          </ul>
          <p>
            A file can be tagged with more than one category at once (e.g. a
            Deliverable that is also Public, or a Report tied to WP3).
            Everything except Public Documents and Deliverables (public ones)
            requires visitors to be logged in to view &ndash; use those for
            internal project material.
          </p>
        </section>

        <section className="text-section">
          <h2>5. Finding and Managing Uploaded Documents</h2>
          <p>
            Page: <strong>/search</strong> (menu: Documents &gt; Search
            Database, login required)
          </p>
          <ul>
            <li>
              Search and filter across all uploaded documents, not just one
              category.
            </li>
            <li>
              Click a file to open or download it (PDFs and images open in a
              viewer; other types download directly).
            </li>
            <li>
              Click Delete next to a file you uploaded to remove it
              permanently. This cannot be undone.
            </li>
          </ul>
        </section>

        <section className="text-section">
          <h2>6. Quick Reference</h2>
          <div className="info-grid">
            <div className="info-item">
              <strong>Log in:</strong> navigation bar login button
            </div>
            <div className="info-item">
              <strong>Publish / edit / delete news:</strong> /settings
              (approved editors only)
            </div>
            <div className="info-item">
              <strong>Upload a document:</strong> /Upload (any logged-in
              account)
            </div>
            <div className="info-item">
              <strong>Browse everything you can see:</strong> /search
            </div>
            <div className="info-item">
              <strong>Public-facing documents:</strong> /public_docs (no
              login needed)
            </div>
            <div className="info-item">
              <strong>Public deliverables:</strong> /deliverables_public (no
              login needed)
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Manual;
